import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "data-storage",
  kind: "horizontal",
  title: "Storage Platform Engineering",
  shortTitle: "Storage Platforms",
  status: "draft",
  tier: "experience",
  seoTitle: "Storage Platform Engineering | General Applications",
  seoDescription:
    "Storage platform engineering for file, block, object, on-premises, cloud, and hybrid systems with clear operational controls.",
  summary:
    "Storage platforms that connect data structures, access, protection, observability, administration, and recovery into a system people can operate with confidence.",
  image: "/images/capabilities/data-search-knowledge-hero.webp",
  relatedCapabilitySlugs: [
    "data-search-knowledge",
    "software-architecture-engineering",
  ],
  relatedIndustrySlugs: [
    "enterprise",
    "energy",
    "military",
    "media-entertainment",
  ],
  relatedPostSlugs: [
    "raid-is-a-failure-budget",
    "zfs-is-a-storage-system",
    "lossy-networks-and-edge-spools",
    "replicating-without-a-database",
  ],
  engineering: [
    "On-premises, cloud, and hybrid storage architectures",
    "File, block, object, and application-managed storage structures and access paths",
    "ZFS storage platforms, appliance hardware, pool topology, and administration tooling",
    "Snapshot, replication, archival, air-gapped backup, and recovery workflows",
    "Capacity, performance, filesystem and dataset tuning, access control, and lifecycle design",
    "Storage health telemetry, integrity checks, scrub automation, and operational runbooks",
  ],
  phases: {
    study: {
      focus:
        "Establish what data exists, how it is accessed, what loss or delay would mean, and who must operate and recover it before selecting a storage approach.",
      produces: [
        "Data inventory, classification, lifecycle, and access-pattern model",
        "Capacity, performance, availability, and recovery requirement analysis",
        "On-premises, cloud, and hybrid constraint assessment",
        "Operational ownership, support, and recovery-readiness map",
      ],
      fit: {
        good: [
          "Organizations choosing, replacing, or scaling storage that supports operational applications, data pipelines, archives, or technical content",
          "Teams whose storage decisions have grown from capacity purchases into reliability, access, recovery, and administration problems",
        ],
        poor: [
          "A commodity purchase with no defined data, access, recovery, or operating requirements",
          "A request to treat backup, retention, and recovery as somebody else's responsibility",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the data structures, location, protection model, and operating boundaries before selecting products or configuring pools.",
      produces: [
        "Storage architecture across file, block, object, database, and archive needs",
        "On-premises, cloud, or hybrid placement rationale",
        "Protection, retention, replication, and recovery model",
        "Administration, access-control, and monitoring design",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the media, capacity model, data layout, interfaces, automation, and recovery procedures that make the architecture operable.",
      produces: [
        "Media, capacity, performance, and growth plan",
        "Filesystem, volume, object, or database data-organization specifications",
        "Snapshot, replication, archival, and restoration procedures",
        "Monitoring, alerting, administration, and support runbooks",
      ],
    },
    implementation: {
      focus:
        "Build or migrate the storage system with the controls, verification, and operating material needed to keep it useful after handoff.",
      produces: [
        "Provisioned storage structures and access paths",
        "Verified protection, restore, and recovery workflows",
        "Capacity and health observability for operators",
        "Administration and support materials tied to the deployed design",
      ],
    },
  },
};
